// src/shared/pages/AdminPage.tsx
// Admin page for user and department management — nova identidade visual (Tailwind + UI kit)

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGlobalAuth } from '../contexts/GlobalAuthContext';
import { isAdminRole } from '../lib/roles';
import { projectManagementAPI, User, Department, CreateUserRequest, UpdateUserRequest } from '../services/api';
import {
  Button,
  Card,
  Badge,
  Input,
  Select,
  Textarea,
  Modal,
  Tabs,
  EmptyState,
  Spinner,
  Table,
  Column,
} from '../components/ui';
import './AdminPage.css';

type TabType = 'users' | 'departments';

interface AdminPageProps {
  initialTab?: TabType;
  embedded?: boolean;
}

const roleLabel = (role: string): string =>
  role === 'admin' ? 'Admin' :
  role === 'director' ? 'Diretor' :
  role === 'manager' ? 'Gerente' :
  role === 'supervisor' ? 'Supervisor' : 'Colaborador';

const roleTone = (role: string): 'brand' | 'info' | 'warning' | 'success' | 'neutral' =>
  role === 'admin' ? 'brand' :
  role === 'director' ? 'info' :
  role === 'manager' ? 'warning' :
  role === 'supervisor' ? 'success' : 'neutral';

const AdminPage: React.FC<AdminPageProps> = ({ initialTab = 'users', embedded = false }) => {
  const navigate = useNavigate();
  const { user: currentUser, logout } = useGlobalAuth();
  const [activeTab, setActiveTab] = useState<TabType>(initialTab);

  // Users state
  const [users, setUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);

  // Departments state
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loadingDepartments, setLoadingDepartments] = useState(true);
  const [showDepartmentModal, setShowDepartmentModal] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState<Department | null>(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  // Check if user is admin.
  // service_roles não concede privilégio (o backend também deixou de aceitar).
  const isAdmin = isAdminRole(currentUser?.role);

  useEffect(() => {
    if (!isAdmin && !embedded) {
      navigate('/quality-management');
    }
  }, [isAdmin, navigate, embedded]);

  // Só carrega dados administrativos se o usuário for de fato admin —
  // antes as chamadas disparavam antes/independente da checagem.
  useEffect(() => {
    if (!isAdmin) return;
    loadUsers();
    loadDepartments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const loadUsers = async () => {
    try {
      setLoadingUsers(true);
      const data = await projectManagementAPI.getAllUsers();
      setUsers(data);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadDepartments = async () => {
    try {
      setLoadingDepartments(true);
      const data = await projectManagementAPI.getDepartments();
      setDepartments(data);
    } catch (error) {
      console.error('Error loading departments:', error);
    } finally {
      setLoadingDepartments(false);
    }
  };

  const handleCreateUser = () => {
    setEditingUser(null);
    setShowUserModal(true);
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setShowUserModal(true);
  };

  const handleDeleteUser = async (userId: number) => {
    if (!window.confirm('Tem certeza de que deseja desativar este usuário?')) return;

    try {
      await projectManagementAPI.deleteUser(userId);
      loadUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Erro ao desativar usuário');
    }
  };

  const handleActivateUser = async (userId: number) => {
    try {
      await projectManagementAPI.activateUser(userId);
      loadUsers();
    } catch (error) {
      console.error('Error activating user:', error);
      alert('Erro ao ativar usuário');
    }
  };

  const handleCreateDepartment = () => {
    setEditingDepartment(null);
    setShowDepartmentModal(true);
  };

  const handleEditDepartment = (dept: Department) => {
    setEditingDepartment(dept);
    setShowDepartmentModal(true);
  };

  const handleDeleteDepartment = async (deptId: number) => {
    if (!window.confirm('Tem certeza de que deseja excluir este departamento?')) return;

    try {
      await projectManagementAPI.deleteDepartment(deptId);
      loadDepartments();
    } catch (error) {
      console.error('Error deleting department:', error);
      alert('Erro ao excluir departamento');
    }
  };

  // Filter users
  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = !roleFilter || user.role === roleFilter;
    const matchesStatus = statusFilter === '' ||
      (statusFilter === 'active' && user.is_active) ||
      (statusFilter === 'inactive' && !user.is_active);
    return matchesSearch && matchesRole && matchesStatus;
  });

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center p-8">
        <Card padding="lg" className="w-full max-w-md text-center" data-testid="admin-access-denied">
          <EmptyState
            icon={
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            }
            title="Acesso negado"
            description="Você não tem permissão para acessar esta página."
          />
        </Card>
      </div>
    );
  }

  const userColumns: Column<User>[] = [
    { key: 'name', header: 'Nome', render: (u) => <span data-testid="user-row" className="font-medium text-foreground">{u.name}</span> },
    { key: 'username', header: 'Usuário', render: (u) => <span className="text-foreground-muted">{u.username}</span> },
    { key: 'email', header: 'E-mail', render: (u) => <span className="text-foreground-muted">{u.email}</span> },
    { key: 'role', header: 'Função', render: (u) => <Badge tone={roleTone(u.role)}>{roleLabel(u.role)}</Badge> },
    {
      key: 'department',
      header: 'Departamento',
      render: (u) => departments.find(d => d.id === u.department_id)?.name || '-',
    },
    {
      key: 'status',
      header: 'Status',
      render: (u) => (
        <Badge tone={u.is_active ? 'success' : 'neutral'} dot>
          {u.is_active ? 'Ativo' : 'Inativo'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: 'Ações',
      align: 'right',
      render: (u) => (
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={() => handleEditUser(u)} data-testid="user-reset-password">
            Editar
          </Button>
          {u.is_active ? (
            <Button
              variant="ghost"
              size="sm"
              className="text-danger hover:bg-danger/10"
              onClick={() => handleDeleteUser(u.id)}
              disabled={u.id === currentUser?.id}
              data-testid="user-toggle-active"
            >
              Desativar
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              className="text-success hover:bg-success/10"
              onClick={() => handleActivateUser(u.id)}
              data-testid="user-toggle-active"
            >
              Ativar
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className={embedded ? '' : 'min-h-screen bg-background'}>
      {!embedded && (
        <header className="flex items-center justify-between border-b border-border bg-surface-1 px-6 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/quality-management')}>
              Voltar
            </Button>
            <h1 className="font-display text-xl font-semibold text-foreground">Administração</h1>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-base text-foreground-muted">{currentUser?.name}</span>
            <Button variant="secondary" size="sm" onClick={logout}>Sair</Button>
          </div>
        </header>
      )}

      <div className={embedded ? '' : 'mx-auto max-w-[1280px] px-6 py-6'}>
        <Tabs
          className="mb-6"
          value={activeTab}
          onChange={(v) => setActiveTab(v as TabType)}
          tabs={[
            { value: 'users', label: <span data-testid="admin-tab-users">Usuários</span> },
            { value: 'departments', label: <span data-testid="admin-tab-departments">Departamentos</span> },
          ]}
        />

        {activeTab === 'users' && (
          <div className="flex flex-col gap-6">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div className="flex flex-wrap items-end gap-3">
                <Input
                  type="text"
                  placeholder="Buscar usuários..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  containerClassName="w-64"
                />
                <Select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  containerClassName="w-48"
                >
                  <option value="">Todas as funções</option>
                  <option value="admin">Administrador</option>
                  <option value="director">Diretor</option>
                  <option value="manager">Gerente</option>
                  <option value="supervisor">Supervisor</option>
                  <option value="employee">Colaborador</option>
                </Select>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  containerClassName="w-40"
                >
                  <option value="">Todos os status</option>
                  <option value="active">Ativo</option>
                  <option value="inactive">Inativo</option>
                </Select>
              </div>
              <Button onClick={handleCreateUser} data-testid="user-create">
                + Novo Usuário
              </Button>
            </div>

            {loadingUsers ? (
              <div className="flex items-center justify-center gap-3 py-16 text-base text-foreground-muted">
                <Spinner /> Carregando usuários...
              </div>
            ) : (
              <Table
                columns={userColumns}
                data={filteredUsers}
                rowKey={(u) => u.id}
                empty="Nenhum usuário encontrado."
              />
            )}
          </div>
        )}

        {activeTab === 'departments' && (
          <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between gap-4">
              <h2 className="font-display text-lg font-semibold text-foreground">Departamentos</h2>
              <Button onClick={handleCreateDepartment} data-testid="dept-create">
                + Novo Departamento
              </Button>
            </div>

            {loadingDepartments ? (
              <div className="flex items-center justify-center gap-3 py-16 text-base text-foreground-muted">
                <Spinner /> Carregando departamentos...
              </div>
            ) : departments.length === 0 ? (
              <Card padding="none">
                <EmptyState
                  icon={
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="4" y="2" width="16" height="20" rx="2" />
                      <path d="M9 22v-4h6v4M9 6h.01M15 6h.01M9 10h.01M15 10h.01M9 14h.01M15 14h.01" />
                    </svg>
                  }
                  title="Nenhum departamento"
                  description="Crie o primeiro departamento para começar."
                />
              </Card>
            ) : (
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {departments.map(dept => (
                  <Card key={dept.id} padding="lg" className="flex flex-col gap-3">
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="font-display text-base font-semibold text-foreground">{dept.name}</h3>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleEditDepartment(dept)}>
                          Editar
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-danger hover:bg-danger/10"
                          onClick={() => handleDeleteDepartment(dept.id)}
                        >
                          Excluir
                        </Button>
                      </div>
                    </div>
                    <p className="text-base text-foreground-muted">
                      {dept.description || 'Sem descrição'}
                    </p>
                    <div className="mt-auto">
                      <Badge tone="neutral">{dept.user_count || 0} usuários</Badge>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* User Modal */}
      {showUserModal && (
        <UserModal
          user={editingUser}
          departments={departments}
          onClose={() => setShowUserModal(false)}
          onSave={() => {
            setShowUserModal(false);
            loadUsers();
          }}
        />
      )}

      {/* Department Modal */}
      {showDepartmentModal && (
        <DepartmentModal
          department={editingDepartment}
          users={users}
          onClose={() => setShowDepartmentModal(false)}
          onSave={() => {
            setShowDepartmentModal(false);
            loadDepartments();
          }}
        />
      )}
    </div>
  );
};

// User Modal Component
interface UserModalProps {
  user: User | null;
  departments: Department[];
  onClose: () => void;
  onSave: () => void;
}

const UserModal: React.FC<UserModalProps> = ({ user, departments, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    username: user?.username || '',
    name: user?.name || '',
    email: user?.email || '',
    password: '',
    role: user?.role || 'employee',
    department_id: user?.department_id || '',
    services: user?.services || ['management'],
    is_active: user?.is_active ?? true
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      if (user) {
        // Update existing user
        const updateData: UpdateUserRequest = {
          name: formData.name,
          email: formData.email,
          role: formData.role,
          department_id: formData.department_id ? Number(formData.department_id) : undefined,
          services: formData.services,
          is_active: formData.is_active
        };
        await projectManagementAPI.updateUser(user.id, updateData);

        // Update password if provided
        if (formData.password) {
          await projectManagementAPI.updateUserPassword(user.id, formData.password);
        }
      } else {
        // Create new user
        const createData: CreateUserRequest = {
          username: formData.username,
          name: formData.name,
          email: formData.email,
          password: formData.password,
          role: formData.role,
          department_id: formData.department_id ? Number(formData.department_id) : undefined,
          services: formData.services,
          is_active: formData.is_active
        };
        await projectManagementAPI.createUser(createData);
      }
      onSave();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar usuário');
    } finally {
      setSaving(false);
    }
  };


  return (
    <Modal
      isOpen
      onClose={onClose}
      title={user ? 'Editar Usuário' : 'Novo Usuário'}
      size="lg"
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>Cancelar</Button>
          <Button type="submit" form="user-modal-form" loading={saving} disabled={saving}>
            Salvar
          </Button>
        </>
      }
    >
      <form id="user-modal-form" onSubmit={handleSubmit} className="flex flex-col gap-4">
        {error && (
          <div className="rounded-lg border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger">
            {error}
          </div>
        )}

        <Input
          label="Usuário"
          type="text"
          value={formData.username}
          onChange={e => setFormData({ ...formData, username: e.target.value })}
          required
          disabled={!!user}
          data-testid="user-username-input"
        />

        <Input
          label="Nome"
          type="text"
          value={formData.name}
          onChange={e => setFormData({ ...formData, name: e.target.value })}
          required
          data-testid="user-name-input"
        />

        <Input
          label="E-mail"
          type="email"
          value={formData.email}
          onChange={e => setFormData({ ...formData, email: e.target.value })}
          required
          data-testid="user-email-input"
        />

        <Input
          label={user ? 'Nova Senha (deixe em branco para manter)' : 'Senha'}
          type="password"
          value={formData.password}
          onChange={e => setFormData({ ...formData, password: e.target.value })}
          required={!user}
          data-testid="user-password-input"
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Select
            label="Função"
            value={formData.role}
            onChange={e => setFormData({ ...formData, role: e.target.value })}
            data-testid="user-role-input"
          >
            <option value="employee">Colaborador</option>
            <option value="supervisor">Supervisor</option>
            <option value="manager">Gerente</option>
            <option value="director">Diretor</option>
            <option value="admin">Administrador</option>
          </Select>

          <Select
            label="Departamento"
            value={formData.department_id}
            onChange={e => setFormData({ ...formData, department_id: e.target.value })}
          >
            <option value="">Sem departamento</option>
            {departments.map(dept => (
              <option key={dept.id} value={dept.id}>{dept.name}</option>
            ))}
          </Select>
        </div>

        <label className="flex cursor-pointer items-center gap-2 text-base text-foreground">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-border text-brand focus:ring-ring/30"
            checked={formData.is_active}
            onChange={e => setFormData({ ...formData, is_active: e.target.checked })}
          />
          Usuário ativo
        </label>
      </form>
    </Modal>
  );
};

// Department Modal Component
interface DepartmentModalProps {
  department: Department | null;
  users: User[];
  onClose: () => void;
  onSave: () => void;
}

const DepartmentModal: React.FC<DepartmentModalProps> = ({ department, users, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: department?.name || '',
    description: department?.description || '',
    manager_user_id: department?.manager_user_id || ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const data = {
        name: formData.name,
        description: formData.description || undefined,
        manager_user_id: formData.manager_user_id ? Number(formData.manager_user_id) : undefined
      };

      if (department) {
        await projectManagementAPI.updateDepartment(department.id, data);
      } else {
        await projectManagementAPI.createDepartment(data);
      }
      onSave();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar departamento');
    } finally {
      setSaving(false);
    }
  };

  // Filter users who can be managers
  const managerCandidates = users.filter(u =>
    u.is_active && ['admin', 'director', 'manager', 'supervisor'].includes(u.role)
  );

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={department ? 'Editar Departamento' : 'Novo Departamento'}
      size="md"
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>Cancelar</Button>
          <Button type="submit" form="dept-modal-form" loading={saving} disabled={saving}>
            Salvar
          </Button>
        </>
      }
    >
      <form id="dept-modal-form" onSubmit={handleSubmit} className="flex flex-col gap-4">
        {error && (
          <div className="rounded-lg border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger">
            {error}
          </div>
        )}

        <Input
          label="Nome"
          type="text"
          value={formData.name}
          onChange={e => setFormData({ ...formData, name: e.target.value })}
          required
        />

        <Textarea
          label="Descrição"
          value={formData.description}
          onChange={e => setFormData({ ...formData, description: e.target.value })}
          rows={3}
        />

        <Select
          label="Gerente"
          value={formData.manager_user_id}
          onChange={e => setFormData({ ...formData, manager_user_id: e.target.value })}
        >
          <option value="">Nenhum gerente atribuído</option>
          {managerCandidates.map(user => (
            <option key={user.id} value={user.id}>{user.name}</option>
          ))}
        </Select>
      </form>
    </Modal>
  );
};

export default AdminPage;
